import {
  ConfiguratorTranslateFn,
  IConfiguratorAnswers,
  IConfiguratorBathroom
} from '../models/configurator.model';
import { BATHROOMS_KEY, ERoom, getBathroomCount } from '../config/configurator-predicates';

const getBathrooms = (answers: IConfiguratorAnswers): IConfiguratorBathroom[] =>
  (answers[BATHROOMS_KEY] as IConfiguratorBathroom[]) || [];

/** Names given to the bathrooms (as in the floor plan), in their order. */
export const getBathroomNames = (answers: IConfiguratorAnswers): string[] =>
  getBathrooms(answers)
    .map((bathroom) => bathroom?.name)
    .filter(Boolean);

/** Name of bathroom `index` in the summary: its own name, otherwise "Bad 2" or just "Bad". */
export const getBathroomSummaryName = (
  answers: IConfiguratorAnswers,
  index: number,
  translate: ConfiguratorTranslateFn
): string =>
  getBathrooms(answers)[index]?.name ||
  (getBathroomCount(answers) > 1
    ? translate('ProjectConfiguratorV2.Bathroom.titleNumbered', { number: index + 1 })
    : translate('ProjectConfiguratorV2.Bathroom.title'));

/**
 * Removes bathroom `index`: the following bathrooms move up (with their names) and the counted bathrooms
 * in `rooms` go down by one. Returns new answers; the input is not changed.
 */
export const removeBathroom = (answers: IConfiguratorAnswers, index: number): IConfiguratorAnswers => {
  const result: IConfiguratorAnswers = { ...answers };

  const bathrooms = [...((answers[BATHROOMS_KEY] as unknown[]) || [])];
  bathrooms.splice(index, 1);
  if (bathrooms.length) {
    result[BATHROOMS_KEY] = bathrooms;
  } else {
    delete result[BATHROOMS_KEY];
  }

  const rooms = { ...(answers.rooms || {}) };
  const count = (rooms[ERoom.BATHROOM] || 0) - 1;
  if (count > 0) {
    rooms[ERoom.BATHROOM] = count;
  } else {
    delete rooms[ERoom.BATHROOM];
  }
  if (Object.keys(rooms).length) {
    result.rooms = rooms;
  } else {
    delete result.rooms;
  }

  return result;
};
